<template>
  <div :class="$style.box">
    <SectionTitle :class="$style.box__title">引受目安</SectionTitle>
    <div :class="$style.box__content">
      <DocumentList
        :corporations="filteredCorporations"
        @click="clickDocument"
      />

      <DocumentModal
        :isOpened="isModalOpened"
        :fileUrl="fileUrl"
        @close="closeModal"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import SectionTitle from '@/components/atoms/SectionTitle.vue'
import {
  type CoverageViewCorporationFragment,
  type CoverageViewDocumentFragment,
  useGetCoverageViewDocumentFileUrlLazyQuery,
} from './CoverageView.generated'
import DocumentList, {
  type Corporation,
  type Document,
} from './DocumentList.vue'
import DocumentModal from './DocumentModal.vue'
import { computed, ref } from 'vue'
import { getEntitiesByUnionArray } from '@/libs/utilities/graphql'
import { useLogger } from '@/logger/logger'

const props = defineProps<{
  corporations: CoverageViewCorporationFragment[]
}>()

const fileUrl = ref<string | null>(null)
const isModalOpened = ref<boolean>(false)

const logger = useLogger()

const closeModal = () => {
  fileUrl.value = null
  isModalOpened.value = false
}

const { load, onResult } = useGetCoverageViewDocumentFileUrlLazyQuery({
  ids: [],
})

onResult(result => {
  fileUrl.value =
    result.data?.solicitorCloudCoverageDocumentVersionByIds?.[0].fileUrl ?? null
})

const clickDocument = async ({
  documentVersionId: id,
}: {
  documentVersionId: CoverageViewDocumentFragment['id']
}) => {
  isModalOpened.value = true

  load(
    undefined,
    {
      ids: [id],
    },
    {
      fetchPolicy: 'no-cache',
    }
  )

  logger.ltlPeople.event.clickSolicitorCloudDocument(logger.buildMeta(), {
    data: { id, type: 'coverage-document' },
  })
}

const filteredCorporations = computed<Corporation[]>((): Corporation[] => {
  return props.corporations.reduce<Corporation[]>(
    (accumulator, { documents, ...corporation }) => {
      const convertedDocuments =
        getEntitiesByUnionArray<CoverageViewDocumentFragment>(
          documents,
          'SolicitorCloudCoverageDocument'
        )
          .filter(({ currentDocumentVersion }) => currentDocumentVersion)
          .map((item): Document => ({
            documentVersionId: item.currentDocumentVersion!.id,
            title: item.title,
            page: 0,
            publishedAt: item.currentDocumentVersion!.publishedAt,
          }))

      return convertedDocuments.length > 0
        ? [
            ...accumulator,
            {
              ...corporation,
              documents: convertedDocuments,
            },
          ]
        : accumulator
    },
    []
  )
})
</script>

<style lang="scss" module>
.box {
  padding: 20px;
  border-radius: 10px;
  width: 100%;
  background-color: $color-surface-primary;

  &__content {
    display: flex;
    flex-direction: column;
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid $color-border-low-emphasis;
  }
}
</style>
